<template>
    <div class="p-6">
        <vx-card class="shadow-lg">
            <div class="p-6">
                <!-- Header -->
                <div class="mb-6">
                    <h3 class="text-xl font-semibold text-gray-800 mb-2">Training Manual Settings</h3>
                    <p class="text-sm text-gray-600">Upload and manage your company's training manual</p>
                </div>

                <!-- Upload Section -->
                <div class="mb-6">
                    <label class="block text-sm font-medium text-gray-700 mb-3">
                        <!-- Upload Training Manual -->
                    </label>

                    <!-- Full clickable area wrapped in label -->
                    <label
                        class="cursor-pointer border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors block">
                        <div class="mb-4">
                            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor"
                                viewBox="0 0 48 48">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" />
                            </svg>
                        </div>

                        <div class="mb-4">
                            <p class="text-sm text-gray-600 mb-1">
                                <span class="font-medium text-blue-600">Click to upload</span> your PDF file
                            </p>
                            <p class="text-xs text-gray-500">PDF files only</p>
                        </div>

                        <!-- Hidden file input -->
                        <input ref="fileInput" class="hidden" type="file" accept=".pdf"
                            @change="fileUploader($event)" />
                    </label>

                    <div v-if="uploading" class="mt-3">
                        <p class="text-sm text-blue-600">Uploading...</p>
                    </div>
                </div>


                <!-- Current Manual Section -->
                <div v-if="settings.training_manual" class="border rounded-lg p-4 bg-gray-50" @click="viewDocument">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center space-x-3">
                            <svg class="h-8 w-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd"
                                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                    clip-rule="evenodd" />
                            </svg>

                            <div>
                                <p class="text-sm font-medium text-gray-900">Current Training Manual</p>
                                <p class="text-xs text-gray-500">{{ getFileName(settings.training_manual) }}</p>
                            </div>
                        </div>

                        <div class="flex space-x-2">
                            <vs-button @click.stop="viewDocument" class="">
                                View
                            </vs-button>
                        </div>
                    </div>
                </div>

                <!-- No Manual State -->
                <div v-else class="text-center py-6">
                    <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p class="text-sm text-gray-500">No training manual uploaded yet</p>
                </div>
            </div>
        </vx-card>
    </div>
</template>

<script>
import { RepositoryFactory } from '@/repository/RepositoryFactory';
const SettingsRepository = RepositoryFactory.get('landlord_settings');

export default {
    name: "GeneralSettings",
    data() {
        return {
            settings: {
                training_manual: ''
            },
            uploading: false
        }
    },
    async mounted() {
        await this.fetchSettings()
    },
    methods: {
        async fetchSettings() {
            try {
                const { data } = (await SettingsRepository.getListOfSettings()).data
                const keys = Object.keys(data);
                for (const key of keys) {
                    data[key].elements.forEach((x) => {
                        this.settings[x.name] = x.value ? x.value : ''
                    })
                }
            } catch (error) {
                console.error('Error fetching settings:', error)
            }
        },

        async fileUploader(event) {
            try {
                const file = event.target.files[0]
                if (!file) return

                this.uploading = true

                let formData = new FormData();
                formData.append('file', file);
                formData.append('path', 'training_manuals');

                const file_name = (await SettingsRepository.uploadFile(formData)).data.data.file
                const payload = {
                    training_manual: file_name
                }

                await SettingsRepository.updateSettings(payload);
                await this.fetchSettings()

                this.uploading = false
            } catch (error) {
                console.error('Upload error:', error)
                this.uploading = false
            }
        },

        triggerFileInput() {
            this.$refs.fileInput.$el.click()
        },

        getFileName(url) {
            if (!url) return ''
            return url.split('/').pop() || 'training_manual.pdf'
        },

        viewDocument() {
            if (this.settings.training_manual) {
                window.open(this.settings.training_manual, '_blank')
            }
        },

        downloadDocument() {
            if (this.settings.training_manual) {
                const link = document.createElement('a')
                link.href = this.settings.training_manual
                link.download = this.getFileName(this.settings.training_manual)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            }
        }
    }
}
</script>

<style scoped>
.transition-colors {
    transition-property: border-color;
    transition-duration: 200ms;
}
</style>